import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { ArrowRight, Code2, Brain, CheckCircle2, FlaskConical } from 'lucide-react';
import Link from 'next/link';
import { isFinnishPage, getServicesBasePath } from '../../lib/blogPaths';

const SERVICES_TECH_CATEGORIES = [
  { label: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'Tailwind CSS'] },
  { label: 'Backend', items: ['Node.js', 'Python', '.NET'] },
  { label: 'Mobile', items: ['React Native', 'Flutter'] },
  { label: 'Cloud', items: ['AWS', 'Azure'] },
  { label: 'Design', items: ['Figma'] },
];

// Compact version of Services AI section – same 4 pillars, shorter copy
const HOME_AI_TOOLS_EN = [
  { icon: Code2, title: 'Code Assistants', description: 'In-editor pair programming so developers stay in flow.' },
  { icon: Brain, title: 'AI Reasoning', description: 'Architecture and design—think through problems before we code.' },
  { icon: CheckCircle2, title: 'Code Review', description: 'AI-powered analysis for faster, consistent reviews.' },
  { icon: FlaskConical, title: 'Testing', description: 'Automated test generation for fewer regressions.' },
];
const HOME_AI_TOOLS_FI = [
  { icon: Code2, title: 'Koodiavustajat', description: 'Pariohjelmointi editorissa, jotta kehittäjät pysyvät flow-tilassa.' },
  { icon: Brain, title: 'Tekoälypäättely', description: 'Arkkitehtuuri ja suunnittelu—mietimme ongelmat läpi ennen koodausta.' },
  { icon: CheckCircle2, title: 'Koodikatselmointi', description: 'Tekoälyavusteinen analyysi nopeampaan ja tasaisempaan katselmointiin.' },
  { icon: FlaskConical, title: 'Testaus', description: 'Automatisoitu testien luonti vähemmän regressioita.' },
];

export function TechnologySection({ variant = 'home' }) {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: variant === 'home' ? '-100px' : '-80px' });
  const isFinnish = variant === 'home' && isFinnishPage(router?.pathname);
  const homeAiTools = isFinnish ? HOME_AI_TOOLS_FI : HOME_AI_TOOLS_EN;

  const isServicesFinnish = variant === 'services' && isFinnishPage(router?.pathname);
  if (variant === 'services') {
    const servicesHeading = isServicesFinnish ? 'Nykyaikaiset teknologiat, toimivat lähestymistavat' : 'Modern Technologies, Proven Approaches';
    const servicesBody = isServicesFinnish
      ? 'Työskentelemme uusimpien teknologioiden parissa noudattaen alan parhaita käytäntöjä. Tiimillämme on syvällistä osaamista koko pino.'
      : 'We work with the latest technologies while following industry best practices. Our team has deep expertise across the full stack.';
    return (
      <section ref={ref} className="section-rhythm bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 min-w-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5 }}
            >
              <h2 className="heading-2 mb-4">{servicesHeading}</h2>
              <p className="text-base text-[#6b7280] leading-relaxed">
                {servicesBody}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {SERVICES_TECH_CATEGORIES.map((cat) => (
                <div key={cat.label}>
                  <h4 className="text-sm font-semibold text-[#374151] mb-3">{cat.label}</h4>
                  <div className="flex flex-wrap gap-2">
                    {cat.items.map((tech) => (
                      <span
                        key={tech}
                        className="px-4 py-2 bg-[#f3f4f6] text-[#374151] rounded-[20px] text-sm font-medium transition-all duration-200 hover:bg-[#e5e7eb] hover:scale-105 cursor-default"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // Home: compact version of Services AI section – same navy, cards, icons, one CTA
  return (
    <section
      ref={ref}
      className="section-rhythm bg-navy text-white relative overflow-hidden [content-visibility:auto]"
    >
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-highlight rounded-full blur-2xl" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="heading-2 mb-4 font-semibold text-white">
            {isFinnish ? 'Miten tekoäly vahvistaa kehitystämme' : 'How AI Enhances Our Development'}
          </h2>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto">
            {isFinnish
              ? 'Jokainen Idention kehittäjä käyttää tekoälyä päivittäisessä työssään—koodin kirjoittamiseen, katselmointiin ja testaukseen. Parempi ohjelmisto, nopeammin.'
              : 'Every developer at Identio uses AI in their daily workflow—for writing, reviewing, and testing code. Better software, faster.'}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {homeAiTools.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.08 }}
                className="ai-tool-card flex flex-col text-center"
              >
                <div className="flex justify-center mb-4">
                  <div className="ai-tool-icon flex-shrink-0">
                    <Icon className="w-6 h-6" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[15px] text-gray-300 leading-relaxed">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.35 }}
          className="text-center"
        >
          <Link
            href={getServicesBasePath(isFinnish ? 'fi' : 'en')}
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-6 py-3 rounded-lg font-semibold transition-all"
          >
            <>
              <span className="md:hidden">{isFinnish ? 'Lue lisää' : 'Learn more'}</span>
              <span className="hidden md:inline">{isFinnish ? 'Lue lisää tekoälylähestymistavastamme' : 'Learn About Our AI Approach'}</span>
            </>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
